import CustomerInfoStep from './CustomerInfoStep';
import CartReviewStep from './CartReviewStep';
import OrderSuccessStep from './OrderSuccessStep';
import ProductCatalogStep from './ProductCatalogStep';
import CheckoutStep from './CheckoutStep';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const OnlineSalesModal = ({ onClose, onSwitchTab }) => {
  const [currentStep, setCurrentStep] = useState('customer'); // 'customer', 'products', 'cart', 'checkout', 'success'
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    state: '',
    city: ''
  });
  const [petPersonalization, setPetPersonalization] = useState('');
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [orderConfirmation, setOrderConfirmation] = useState(null); // Store order details after submission

  // Online product catalog (filtered from main CRM)
  const [productCatalog, setProductCatalog] = useState({
    urns: {},
    jewelry: [],
    keepsakes: []
  });

  // Fetch product data from main CRM (read-only)
  const fetchProductCatalog = async () => {
    try {
      const responses = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/urns`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/jewelry`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/keepsakes`)
      ]);

      const [urnsData, jewelryData, keepsakesData] = await Promise.all(
        responses.map(r => r.json())
      );

      // Filter keepsakes to only fur clippings
      const furClippings = keepsakesData.keepsakes.filter(item => 
        item.name.includes('Fur Clipping')
      );

      setProductCatalog({
        urns: urnsData.urns,
        jewelry: jewelryData.jewelry,
        keepsakes: furClippings
      });
    } catch (error) {
      console.error('Error fetching product catalog:', error);
    }
  };

  React.useEffect(() => {
    fetchProductCatalog();
  }, []);

  // Calculate shipping costs
  const calculateShipping = (cartItems) => {
    let urnShipping = 0;
    let jewelryShipping = 0;
    let keepsakeShipping = 0;

    const urnCount = cartItems.filter(item => item.category === 'urn').length;
    const jewelryCount = cartItems.filter(item => item.category === 'jewelry').length;
    const keepsakeCount = cartItems.filter(item => item.category === 'keepsake').length;

    // Urn shipping: $15 first + $12 each additional
    if (urnCount > 0) {
      urnShipping = 15 + ((urnCount - 1) * 12);
    }

    // Jewelry shipping: $8 first + $4 each additional  
    if (jewelryCount > 0) {
      jewelryShipping = 8 + ((jewelryCount - 1) * 4);
    }

    // Keepsake shipping: $8 first + $4 each additional
    if (keepsakeCount > 0) {
      keepsakeShipping = 8 + ((keepsakeCount - 1) * 4);
    }

    return urnShipping + jewelryShipping + keepsakeShipping;
  };

  const addToCart = (product, category, customPersonalization = '') => {
    const newItem = {
      id: Date.now() + Math.random(),
      name: product.name,
      price: product.price,
      category: category,
      petPersonalization: customPersonalization || petPersonalization || ''
    };
    setCart(prev => [...prev, newItem]);
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price, 0);
  const shipping = calculateShipping(cart);
  const total = subtotal + shipping;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-7xl mx-4 max-h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🛒 Online Sales</h2>
            <p className="text-gray-600">Pet memorial products — direct customer sales</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Top Tab Navigation */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => onSwitchTab && onSwitchTab('orders')}
            className="px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
          >
            📦 Order History
          </button>
          <button
            className="px-4 py-3 text-sm font-semibold text-purple-700 border-b-2 border-purple-600"
          >
            + New Order
          </button>
        </div>

        {/* Progress Steps */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[
              { key: 'customer', label: 'Customer Info', icon: '👤' },
              { key: 'products', label: 'Products', icon: '🛍️' },
              { key: 'cart', label: 'Review Order', icon: '🛒' },
              { key: 'checkout', label: 'Checkout', icon: '💳' },
              { key: 'success', label: 'Complete', icon: '✅' }
            ].map(step => (
              <div key={step.key} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  currentStep === step.key 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  <span className="text-sm">{step.icon}</span>
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  currentStep === step.key ? 'text-indigo-600' : 'text-gray-500'
                }`}>
                  {step.label}
                </span>
                {step.key !== 'checkout' && (
                  <div className="w-16 h-0.5 bg-gray-200 ml-4"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          {currentStep === 'customer' && (
            <CustomerInfoStep 
              customer={customer}
              setCustomer={setCustomer}
              onNext={() => setCurrentStep('products')}
            />
          )}

          {currentStep === 'products' && (
            <ProductCatalogStep
              productCatalog={productCatalog}
              petPersonalization={petPersonalization}
              setPetPersonalization={setPetPersonalization}
              onAddToCart={addToCart}
              onNext={() => setCurrentStep('cart')}
              onBack={() => setCurrentStep('customer')}
            />
          )}

          {currentStep === 'cart' && (
            <CartReviewStep
              cart={cart}
              customer={customer}
              subtotal={subtotal}
              shipping={shipping}
              total={total}
              onRemoveItem={removeFromCart}
              onNext={() => setCurrentStep('checkout')}
              onBack={() => setCurrentStep('products')}
            />
          )}

          {currentStep === 'checkout' && (
            <CheckoutStep
              cart={cart}
              customer={customer}
              total={total}
              onBack={() => setCurrentStep('cart')}
              onOrderSuccess={(orderData) => {
                setOrderConfirmation(orderData);
                setCurrentStep('success');
              }}
            />
          )}

          {currentStep === 'success' && (
            <OrderSuccessStep
              orderConfirmation={orderConfirmation}
              customer={customer}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Customer Info Step Component

export default OnlineSalesModal;
